import { ButtonV2, Divider, Flex, TextV2 } from '@withjoy/joykit';
import React, { useCallback } from 'react';
import { JsonView } from '../JsonView';
import { QuarantineStatus, useApproveContactMessageBatchMutation, useGetQuarantinedMessagesQuery, useRejectContactMessageBatchMutation } from '@graphql/generated';
import { Username } from '../Username';
import { EventWebsiteButton } from '../EventWebsiteButton';
import { extractMessageField } from '../../utils/extractMessageField';

type ExpandedRowViewProps = {
  rowID: string;
  onGoBackClicked: () => void;
};

const MessageValue = (props: { label: string; value: unknown }) => {
  return (
    <TextV2 typographyVariant="hed3">
      {props.label}{' '}
      <span>
        <TextV2
          typographyVariant="hed1"
          display="inline-block"
        >
          {props.value}
        </TextV2>
      </span>
    </TextV2>
  );
};

export const ExpandedRowView = (props: ExpandedRowViewProps) => {
  const { onGoBackClicked, rowID } = props;

  const { data: queryResults } = useGetQuarantinedMessagesQuery({
    variables: {
      filter: {
        id: rowID,
      },
      sort: {},
    },
    batchMode: 'off',
  });

  const data = queryResults?.contactQuarantinedMessageBatchContainer.pagedResults.edges[0].node;

  const [approveMessageMutation] = useApproveContactMessageBatchMutation();
  const [rejectMessageMutation] = useRejectContactMessageBatchMutation();

  const onApproveClick = useCallback(() => {
    if (data) {
      approveMessageMutation({
        variables: {
          approveContactMessageBatchId: data.id,
        },
      }).then(onGoBackClicked);
    }
  }, [approveMessageMutation, data, onGoBackClicked]);

  const onRejectClick = useCallback(() => {
    if (data) {
      rejectMessageMutation({
        variables: {
          rejectContactMessageBatchId: data?.id,
        },
      }).then(onGoBackClicked);
    }
  }, [data, onGoBackClicked, rejectMessageMutation]);

  if (!data) {
    return null;
  }

  const reviewedAtDate = data.reviewedAt ? new Date(data.reviewedAt) : null;

  const subject = extractMessageField(data.messageBatchPayload, 'subject');
  const messages = extractMessageField(data.messageBatchPayload, 'messages') as unknown[];
  const content = extractMessageField(data.messageBatchPayload, 'message', 'string');
  const messagesCount = Array.isArray(messages) ? messages.length : 0;

  return (
    <Flex flexDirection="column">
      <Flex>
        <ButtonV2 onClick={onGoBackClicked}>Go Back</ButtonV2>
        {data.quarantineStatus === QuarantineStatus.review && (
          <>
            <ButtonV2
              onClick={onApproveClick}
              intent="primary"
            >
              Approve Message
            </ButtonV2>
            <ButtonV2
              onClick={onRejectClick}
              intent="destructive"
            >
              Reject Message
            </ButtonV2>
          </>
        )}
      </Flex>
      {data.eventId && <EventWebsiteButton eventId={data.eventId} />}
      <Divider marginY="0.5rem" />
      <TextV2 typographyVariant="hed6">Message Overview</TextV2>
      <Flex flexDirection="column">
        <MessageValue
          label="Subject:"
          value={subject}
        />
        <MessageValue
          label="Number of Recipients:"
          value={messagesCount}
        />
        <MessageValue
          label="Content:"
          value={content}
        />
      </Flex>
      <Divider marginY="0.5rem" />
      <TextV2 typographyVariant="hed6">The Details</TextV2>
      {reviewedAtDate && data.reviewedByUserId && (
        <>
          <TextV2>
            Reviewed at {reviewedAtDate.toLocaleDateString()} {reviewedAtDate.toLocaleString()}
          </TextV2>
          <Flex gap="0.25rem">
            <TextV2>Message was {data.quarantineStatus} by </TextV2> <Username id={data.reviewedByUserId} />
          </Flex>
        </>
      )}
      {data.spamIndicatorsPayload && <JsonView data={data.spamIndicatorsPayload} />}
      <JsonView data={data.messageBatchPayload} />
    </Flex>
  );
};
